<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { auth, googleProvider } from '../../firebase'
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail
} from 'firebase/auth'
import { useUserStore } from '../../stores/userStore'
import api from '../../api'
import logoUrl from '../../assets/riffdraft_logo.png'

defineProps<{
  pendingInviteBand?: string | null
}>()

const userStore = useUserStore()

// Email/Password states
const showEmailForm = ref(false)
const isSignUp = ref(false)
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const displayName = ref('')
const errorMessage = ref('')
const loading = ref(false)
const nameAvailable = ref<boolean | null>(null)
const nameChecking = ref(false)
const showForgotPassword = ref(false)
const resetEmail = ref('')
const resetSent = ref(false)
const resetLoading = ref(false)
const awaitingVerification = ref(false)
const verificationResent = ref(false)

let nameCheckTimeout: any = null
watch(displayName, (name) => {
  nameAvailable.value = null
  if (nameCheckTimeout) clearTimeout(nameCheckTimeout)
  if (!name.trim() || !isSignUp.value) return
  nameCheckTimeout = setTimeout(async () => {
    nameChecking.value = true
    try {
      const response = await api.get(`/users/check-name?name=${encodeURIComponent(name.trim())}`)
      nameAvailable.value = response.data.available
    } catch {
      nameAvailable.value = null
    } finally {
      nameChecking.value = false
    }
  }, 400)
})

const firebaseErrors: Record<string, string> = {
  'auth/email-already-in-use': 'This email is already registered. Try logging in instead.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/user-not-found': 'No account found with this email.',
  'auth/wrong-password': 'Incorrect password.',
  'auth/invalid-credential': 'Invalid email or password.',
  'auth/weak-password': 'Password must be at least 6 characters.',
  'auth/too-many-requests': 'Too many attempts. Please try again later.',
  'auth/popup-closed-by-user': 'Sign-in was cancelled.',
  'auth/network-request-failed': 'Network error. Please check your connection.',
}

const cleanError = (msg: string) => {
  const codeMatch = msg.match(/\(auth\/([\w-]+)\)/)
  if (codeMatch) {
    return firebaseErrors[`auth/${codeMatch[1]}`] || msg.replace(/^Firebase:\s*/i, '').replace(/\s*\(auth\/[\w-]+\)\.?$/, '').trim()
  }
  return msg
}

const loginWithGoogle = async () => {
  loading.value = true
  errorMessage.value = ''
  try {
    await signInWithPopup(auth, googleProvider)
  } catch (error: any) {
    errorMessage.value = cleanError(error.message)
    console.error('Error signing in with Google:', error)
  } finally {
    loading.value = false
  }
}

const handleEmailAuth = async () => {
  errorMessage.value = ''
  
  if (isSignUp.value && password.value !== confirmPassword.value) {
    errorMessage.value = "Passwords do not match."
    return
  }

  if (isSignUp.value && nameAvailable.value === false) {
    errorMessage.value = "This display name is already taken."
    return
  }

  loading.value = true
  try {
    if (isSignUp.value) {
      const userCredential = await createUserWithEmailAndPassword(auth, email.value, password.value)

      if (displayName.value) {
        await updateProfile(userCredential.user, { displayName: displayName.value })
      }

      await sendEmailVerification(userCredential.user)
      awaitingVerification.value = true
    } else {
      const userCredential = await signInWithEmailAndPassword(auth, email.value, password.value)

      if (!userCredential.user.emailVerified) {
        awaitingVerification.value = true
        return
      }

      await userStore.syncWithBackend()
    }
  } catch (error: any) {
    errorMessage.value = cleanError(error.message)
    console.error('Email Auth Error:', error)
  } finally {
    loading.value = false
  }
}

const handleForgotPassword = async () => {
  errorMessage.value = ''
  if (!resetEmail.value.trim()) {
    errorMessage.value = 'Please enter your email address.'
    return
  }
  resetLoading.value = true
  try {
    await sendPasswordResetEmail(auth, resetEmail.value.trim())
    resetSent.value = true
  } catch (error: any) {
    errorMessage.value = cleanError(error.message)
  } finally {
    resetLoading.value = false
  }
}

const handleResendVerification = async () => {
  if (!auth.currentUser) return
  verificationResent.value = false
  try {
    await sendEmailVerification(auth.currentUser)
    verificationResent.value = true
  } catch (error: any) {
    errorMessage.value = cleanError(error.message)
  }
}

const handleVerificationLogin = async () => {
  loading.value = true
  errorMessage.value = ''
  try {
    // Reload user to get fresh emailVerified status
    await auth.currentUser?.reload()
    if (auth.currentUser?.emailVerified) {
      // Re-trigger Vue reactivity with the updated user object
      userStore.user = auth.currentUser
      awaitingVerification.value = false
      await userStore.syncWithBackend(displayName.value || undefined)
    } else {
      errorMessage.value = 'Email not yet verified. Please check your inbox and click the verification link.'
    }
  } catch (error: any) {
    errorMessage.value = cleanError(error.message)
  } finally {
    loading.value = false
  }
}

// Auto-detect unverified user returning to the app
onMounted(() => {
  const u = auth.currentUser
  if (u && !u.emailVerified && u.providerData[0]?.providerId === 'password') {
    awaitingVerification.value = true
  }
})

const toggleEmailForm = (signup = false) => {
  showEmailForm.value = true
  isSignUp.value = signup
  errorMessage.value = ''
}
</script>

<template>
  <div class="max-w-md w-full bg-white rounded-xl shadow-lg p-8 border border-slate-200">
    <div class="text-center mb-8">
      <img :src="logoUrl" alt="RiffDraft Logo" class="w-full h-auto mx-auto mb-4" />
    </div>

    <div v-if="pendingInviteBand" class="mb-6 p-4 bg-teal-50 border border-teal-200 rounded-xl text-center">
      <i class="pi pi-users text-teal-600 text-xl mb-2"></i>
      <p class="text-sm text-teal-800 font-medium">You've been invited to join <strong>{{ pendingInviteBand }}</strong>!</p>
      <p class="text-xs text-teal-600 mt-1">Sign in or create an account to continue.</p>
    </div>

    <!-- Email Verification Screen -->
    <div v-if="awaitingVerification" class="space-y-4 text-center">
      <div class="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto">
        <i class="pi pi-envelope text-teal-600 text-2xl"></i>
      </div>
      <h2 class="text-xl font-bold text-slate-800">Verify Your Email</h2>
      <p class="text-sm text-slate-500">We've sent a verification link to <strong>{{ email || auth.currentUser?.email }}</strong>.</p>
      <p class="text-sm text-slate-500">Please check your inbox and click the link to verify your account. If you don't see it, check your spam or trash folder.</p>

      <div v-if="errorMessage" class="p-3 text-sm font-bold text-amber-500 bg-red-50 rounded-lg border border-red-200">
        {{ errorMessage }}
      </div>

      <div v-if="verificationResent" class="p-3 text-sm text-teal-700 bg-teal-50 rounded-lg border border-teal-200">
        Verification email resent!
      </div>

      <div class="pt-2 space-y-2">
        <Button label="I've Verified My Email" class="w-full" @click="handleVerificationLogin" :loading="loading" />
        <Button label="Resend Verification Email" severity="secondary" text class="w-full" @click="handleResendVerification" />
        <Button label="Back" severity="secondary" text class="w-full" @click="awaitingVerification = false; showEmailForm = false" />
      </div>
    </div>

    <!-- Forgot Password Screen -->
    <div v-else-if="showForgotPassword" class="space-y-4">
      <h2 class="text-xl font-semibold text-slate-800 mb-4">Reset Password</h2>

      <div v-if="errorMessage" class="p-3 text-sm font-bold text-amber-500 bg-red-50 rounded-lg border border-red-200">
        {{ errorMessage }}
      </div>

      <div v-if="resetSent" class="p-3 text-sm text-teal-700 bg-teal-50 rounded-lg border border-teal-200">
        Password reset email sent! Check your inbox for a link to reset your password.
      </div>

      <template v-if="!resetSent">
        <p class="text-sm text-slate-500">Enter your email address and we'll send you a link to reset your password.</p>
        <div class="flex flex-col gap-2">
          <label for="resetEmail" class="text-sm font-medium text-slate-700">Email</label>
          <InputText id="resetEmail" v-model="resetEmail" type="email" placeholder="email@example.com" class="w-full" @keyup.enter="handleForgotPassword" />
        </div>
        <Button label="Send Reset Link" class="w-full" @click="handleForgotPassword" :loading="resetLoading" />
      </template>

      <Button label="Back to Login" severity="secondary" text class="w-full" @click="showForgotPassword = false; resetSent = false; errorMessage = ''" />
    </div>

    <div v-else-if="!showEmailForm" class="space-y-4">
      <Button label="Sign In with Google" icon="pi pi-google" class="w-full" severity="secondary" @click="loginWithGoogle" :loading="loading" />
      <Button label="Login with Email" icon="pi pi-envelope" class="w-full" @click="toggleEmailForm(false)" :disabled="loading" />
      
      <div class="relative py-4">
        <div class="absolute inset-0 flex items-center">
          <div class="w-full border-t border-slate-200"></div>
        </div>
        <div class="relative flex justify-center text-sm">
          <span class="px-2 bg-white text-slate-500 italic">No account?</span>
        </div>
      </div>

      <Button label="Create Account" class="w-full" severity="success" outlined @click="toggleEmailForm(true)" :disabled="loading" />
    </div>

    <div v-else class="space-y-4">
      <h2 class="text-xl font-semibold text-slate-800 mb-4">{{ isSignUp ? 'Create Account' : 'Login' }}</h2>
      
      <div v-if="errorMessage" class="p-3 text-sm font-bold text-amber-500 bg-red-50 rounded-lg border border-red-200">
        {{ errorMessage }}
      </div>

      <div class="flex flex-col gap-2">
        <label for="email" class="text-sm font-medium text-slate-700">Email</label>
        <InputText id="email" v-model="email" type="email" placeholder="email@example.com" class="w-full" :disabled="loading" />
      </div>

      <div v-if="isSignUp" class="flex flex-col gap-2">
        <label for="name" class="text-sm font-medium text-slate-700">Display Name <span class="font-normal text-slate-400">(this is what other users will see)</span></label>
        <InputText id="name" v-model="displayName" type="text" placeholder="Your Name" class="w-full" :disabled="loading" />
        <div v-if="displayName.trim()" class="flex items-center gap-1 mt-1">
          <i v-if="nameChecking" class="pi pi-spin pi-spinner text-xs text-slate-400"></i>
          <span v-else-if="nameAvailable === true" class="text-xs text-teal-600"><i class="pi pi-check mr-1"></i>Available</span>
          <span v-else-if="nameAvailable === false" class="text-xs text-red-500"><i class="pi pi-times mr-1"></i>Already taken</span>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label for="password" class="text-sm font-medium text-slate-700">Password</label>
        <Password id="password" v-model="password" :feedback="isSignUp" toggleMask class="w-full" fluid @keyup.enter="handleEmailAuth" :disabled="loading" />
      </div>

      <div v-if="!isSignUp" class="flex justify-end -mt-1">
        <button class="text-xs text-teal-600 hover:text-teal-700 font-medium" @click="showForgotPassword = true; resetEmail = email; errorMessage = ''">Forgot Password?</button>
      </div>

      <div v-if="isSignUp" class="flex flex-col gap-2">
        <label for="confirmPassword" class="text-sm font-medium text-slate-700">Confirm Password</label>
        <Password id="confirmPassword" v-model="confirmPassword" :feedback="false" toggleMask class="w-full" fluid @keyup.enter="handleEmailAuth" :disabled="loading" />
      </div>

      <div class="pt-2 space-y-2">
        <Button :label="isSignUp ? 'Sign Up' : 'Sign In'" class="w-full" @click="handleEmailAuth" :loading="loading" />
        <Button label="Back" severity="secondary" text class="w-full" @click="showEmailForm = false" :disabled="loading" />
      </div>
    </div>
  </div>
</template>
